import React from "react";
import "./FoliosCommandCenterView.css";
import LoaderPremiumLogo from "../../components/LoaderPremiumLogo";
import {
  getFoliosCommandCenter,
  searchFolioRemote
} from "../../services/foliosCommandCenterService";

function fmt(value) {
  return Number(value || 0).toLocaleString("en-US");
}

function signed(value) {
  const n = Number(value || 0);
  return n > 0 ? "+" + fmt(n) : fmt(n);
}

function text(value, fallback = "N/D") {
  const clean = String(value || "").trim();
  return clean || fallback;
}

function norm(value) {
  return String(value || "").trim().toUpperCase();
}

function isCancelacion(item) {
  return norm(item.fuente).includes("CANCELACION") || norm(item.estatus).includes("CANCELADO");
}

function isEntregado(item) {
  return norm(item.estatus).includes("ENTREGADO");
}

function isSobrante(item) {
  return norm(item.estatus).includes("SOBRANTE");
}

function buildFolioDictamen(matches) {
  const rows = Array.isArray(matches) ? matches : [];

  const cancelacion = rows.find(isCancelacion);

  const entregado = rows.find(function (item) {
    return isEntregado(item) && text(item.usuario, "") !== "";
  });

  const sobrante = rows.find(function (item) {
    return isSobrante(item) && text(item.usuario, "") !== "";
  });

  const conUsuario = rows.find(function (item) {
    return text(item.usuario, "") !== "";
  });

  const uso = entregado || sobrante || conUsuario || {};
  const principal = cancelacion || uso || rows[0] || {};

  let riesgo = "SIN DATOS";
  let mensaje = "No hay información suficiente para emitir dictamen.";

  if (rows.length > 0) {
    riesgo = cancelacion ? "ALTO" : "MEDIO";
    mensaje = cancelacion
      ? "El folio aparece cancelado y también tiene trazabilidad en bitácora."
      : "El folio aparece en bitácora, pero no se encontró cancelación directa.";
  }

  return {
    quienUso: uso.usuario || "",
    fuenteUso: uso.fuente || "",
    rangoUso: uso.rango || uso.folios || "",
    estado: cancelacion ? "CANCELADO" : principal.estatus || "ENCONTRADO",
    municipio: principal.municipio || principal.oficina || uso.municipio || uso.oficina || "",
    fecha: principal.fecha || uso.fecha || "",
    causa: cancelacion ? cancelacion.causa : principal.causa || principal.rango || "",
    riesgo,
    mensaje
  };
}

function Kpi(props) {
  return (
    <div className={"folios-kpi " + (props.tone || "")}>
      <span>{props.label}</span>
      <strong>{props.value}</strong>
      {props.hint ? <small>{props.hint}</small> : null}
    </div>
  );
}

function Pill(props) {
  return (
    <span className={"folio-pill " + (props.tone || "")}>
      {props.children}
    </span>
  );
}

function BarChart(props) {
  const rows = Array.isArray(props.rows) ? props.rows.slice(0, props.limit || 8) : [];

  const max = rows.reduce(function (acc, row) {
    return Math.max(acc, Number(row[props.valueKey] || 0));
  }, 0);

  return (
    <div className="exec-chart">
      <div className="exec-chart-head">
        <div>
          <p>{props.eyebrow}</p>
          <h3>{props.title}</h3>
        </div>
        <span>{rows.length} registros</span>
      </div>

      <div className="exec-bars">
        {rows.length === 0 ? (
          <div className="empty-box">Sin datos para este periodo.</div>
        ) : null}

        {rows.map(function (row, index) {
          const value = Number(row[props.valueKey] || 0);
          const width = max > 0 ? Math.max(3, Math.round((value / max) * 100)) : 0;

          return (
            <div className="exec-bar-row" key={String(index)}>
              <div className="exec-bar-line">
                <strong>{text(row.municipio || row.oficina || row.label || row.causa || row.usuario)}</strong>
                <span>{props.format === "signed" ? signed(value) : fmt(value)}</span>
              </div>

              <div className="exec-track">
                <i style={{ width: width + "%" }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FiltersPanel(props) {
  return (
    <aside className="filters-card">
      <div className="filters-head">
        <strong>{props.title || "Filtros"}</strong>
        <button type="button" onClick={props.onClear}>Limpiar</button>
      </div>

      <label className="field">
        <span>Fecha inicio</span>
        <input
          type="date"
          value={props.fechaInicio}
          onChange={function (event) { props.setFechaInicio(event.target.value); }}
        />
      </label>

      <label className="field">
        <span>Fecha fin</span>
        <input
          type="date"
          value={props.fechaFin}
          onChange={function (event) { props.setFechaFin(event.target.value); }}
        />
      </label>

      <button className="apply-btn" type="button" onClick={props.onApply}>
        Aplicar periodo
      </button>

      <label className="field">
        <span>Municipio / oficina</span>
        <select
          value={props.municipio}
          onChange={function (event) { props.setMunicipio(event.target.value); }}
        >
          <option value="TODOS">Todos</option>
          {props.municipios.map(function (item) {
            return <option key={item} value={item}>{item}</option>;
          })}
        </select>
      </label>

      <label className="field">
        <span>Usuario / responsable</span>
        <input
          value={props.usuario}
          onChange={function (event) { props.setUsuario(event.target.value); }}
          placeholder="Buscar usuario..."
        />
      </label>

      <label className="field">
        <span>Texto libre</span>
        <input
          value={props.textoLibre}
          onChange={function (event) { props.setTextoLibre(event.target.value); }}
          placeholder="Municipio, usuario, causa..."
        />
      </label>
    </aside>
  );
}

export default function FoliosCommandCenterView() {
  const [payload, setPayload] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const [activeTab, setActiveTab] = React.useState("executive");

  const [folio, setFolio] = React.useState("");
  const [folioLoading, setFolioLoading] = React.useState(false);
  const [folioError, setFolioError] = React.useState("");
  const [folioSearched, setFolioSearched] = React.useState(false);
  const [folioMatches, setFolioMatches] = React.useState([]);

  const [fechaInicio, setFechaInicio] = React.useState("2026-08-01");
  const [fechaFin, setFechaFin] = React.useState("2026-08-31");
  const [municipio, setMunicipio] = React.useState("TODOS");
  const [usuario, setUsuario] = React.useState("");
  const [textoLibre, setTextoLibre] = React.useState("");

  const [showRanking, setShowRanking] = React.useState(false);
  const [showCancelaciones, setShowCancelaciones] = React.useState(false);
  const [showAuditoria, setShowAuditoria] = React.useState(false);
  const [selectedForensicCase, setSelectedForensicCase] = React.useState(null);
  const [forensicMode, setForensicMode] = React.useState("pendientes");
  const [forensicClassification, setForensicClassification] = React.useState("TODAS");

  async function cargar(force) {
    try {
      setLoading(true);
      setError("");

      const data = await getFoliosCommandCenter({
        force: force === true,
        fechaInicio,
        fechaFin
      });

      setPayload(data);
    } catch (err) {
      console.error(err);
      setError(err.message || "No se pudo cargar Control de Folios.");
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(function () {
    cargar(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function buscarFolio() {
    const clean = String(folio || "").trim();

    setFolioSearched(true);
    setFolioError("");
    setFolioMatches([]);

    if (!clean) return;

    try {
      setFolioLoading(true);
      const result = await searchFolioRemote(clean, {
        fechaInicio,
        fechaFin,
        municipio
      });
      setFolioMatches(Array.isArray(result.matches) ? result.matches : []);
    } catch (err) {
      console.error(err);
      setFolioError(err.message || "No se pudo buscar el folio.");
    } finally {
      setFolioLoading(false);
    }
  }

  function limpiarFiltros() {
    setFechaInicio("");
    setFechaFin("");
    setMunicipio("TODOS");
    setUsuario("");
    setTextoLibre("");
  }

  const summary = payload && payload.summary ? payload.summary : {};
  const municipios = payload && Array.isArray(payload.municipios) ? payload.municipios : [];
  const cancelaciones = payload && Array.isArray(payload.cancelacionesDetalle) ? payload.cancelacionesDetalle : [];
  const sinReporte = payload && Array.isArray(payload.sinReporte) ? payload.sinReporte : [];
  const auditoria = payload && Array.isArray(payload.auditoria) ? payload.auditoria : [];
  const causas = payload && Array.isArray(payload.cancelacionesPorCausa) ? payload.cancelacionesPorCausa : [];
  const usuariosCancelacion = payload && Array.isArray(payload.cancelacionesPorUsuario) ? payload.cancelacionesPorUsuario : [];

  // =========================================================
  // FORENSE V5.1.2
  // =========================================================

  const analisisCarryoverV51 =
    payload && payload.analisisCarryoverV51
      ? payload.analisisCarryoverV51
      : {};

  const casosCarryoverV51 =
    Array.isArray(analisisCarryoverV51.casos)
      ? analisisCarryoverV51.casos
      : [];

  const pendienteV49 =
    Number(summary.pendienteForenseV49 || 0);

  const reduccionV51 =
    Number(summary.reduccionPendienteV51 || 0);

  const pendienteV51 =
    Number(summary.pendienteForenseV51 || 0);

  const casosV51 =
    Number(
      summary.casosCarryoverV51 ||
      summary.casosAplicadosV51 ||
      casosCarryoverV51.length ||
      0
    );

  const deltaV51 =
    Number(summary.deltaControlV51 || 0);

  const integridadV51 =
    text(summary.integridadV51, "N/D");

  const versionV51 =
    text(summary.motorCarryoverV51Version, "N/D");

  // =========================================================
  // CONSOLA FORENSE
  // =========================================================

  const expedientesForenses =
    payload &&
    payload.analisisForenseIntegrado &&
    Array.isArray(payload.analisisForenseIntegrado.expedientes)
      ? payload.analisisForenseIntegrado.expedientes
      : [];

  const expedientesPendientes =
    expedientesForenses.filter(function (row) {
      const estado =
        norm(
          row.estadoForenseV48 ||
          row.estadoForenseV47 ||
          row.estadoForenseV44 ||
          row.estadoForense
        );

      return !estado.includes("CONCILIADO");
    });

  const expedientesConciliados =
    expedientesForenses.filter(function (row) {
      const estado =
        norm(
          row.estadoForenseV48 ||
          row.estadoForenseV47 ||
          row.estadoForenseV44 ||
          row.estadoForense
        );

      return estado.includes("CONCILIADO");
    });

  function forensicEstado(row) {
    return text(
      row.estadoForenseV48 ||
      row.estadoForenseV47 ||
      row.estadoForenseV44 ||
      row.estadoForense,
      "PENDIENTE"
    );
  }

  function forensicClasificacion(row) {
    return text(
      row.clasificacionV47 ||
      row.clasificacionV46 ||
      row.clasificacion ||
      row.causaCierreV48,
      "SIN CLASIFICAR"
    );
  }

  function forensicNivel(row) {
    return text(
      row.nivelEvidenciaV47 ||
      row.nivelEvidenciaV46 ||
      row.nivelEvidenciaProcedencia ||
      row.nivelEvidencia,
      "N/D"
    );
  }

  function forensicPendiente(row) {
    const valores = [
      row.pendienteForenseV49,
      row.pendienteForenseV48,
      row.pendienteForenseV47,
      row.pendienteForenseV46,
      row.pendienteForenseV45,
      row.pendienteForenseV44,
      row.pendienteForenseIntegrado,
      row.pendienteCausalEstricto
    ];

    for (let i = 0; i < valores.length; i += 1) {
      if (
        valores[i] !== undefined &&
        valores[i] !== null &&
        valores[i] !== ""
      ) {
        return Number(valores[i] || 0);
      }
    }

    return 0;
  }

  const forensicClassifications =
    Array.from(
      new Set(
        expedientesForenses
          .map(function (row) {
            return forensicClasificacion(row);
          })
          .filter(Boolean)
      )
    ).sort();

  function forensicMatch(row) {

    const rowMunicipio =
      String(row.municipio || "");

    const allText =
      [
        row.fecha,
        row.municipio,
        row.expedienteId,
        forensicEstado(row),
        forensicClasificacion(row),
        forensicNivel(row),
        row.causaProbableOriginal,
        row.accionOriginal,
        row.accionForense
      ]
        .join(" ")
        .toLowerCase();

    if (
      municipio !== "TODOS" &&
      rowMunicipio !== municipio
    ) {
      return false;
    }

    if (
      forensicClassification !== "TODAS" &&
      forensicClasificacion(row) !== forensicClassification
    ) {
      return false;
    }

    if (
      textoLibre &&
      !allText.includes(textoLibre.toLowerCase())
    ) {
      return false;
    }

    return true;
  }

  const expedientesPendientesFiltrados =
    expedientesPendientes.filter(forensicMatch);

  const expedientesConciliadosFiltrados =
    expedientesConciliados.filter(forensicMatch);

  const municipioForenseMap = {};

  expedientesForenses.forEach(function (row) {

    const key =
      text(row.municipio, "SIN MUNICIPIO");

    if (!municipioForenseMap[key]) {
      municipioForenseMap[key] = {
        municipio: key,
        pendientes: 0,
        conciliados: 0,
        foliosPendientes: 0
      };
    }

    if (forensicEstado(row).includes("CONCILIADO")) {
      municipioForenseMap[key].conciliados += 1;
    }
    else {
      municipioForenseMap[key].pendientes += 1;
      municipioForenseMap[key].foliosPendientes += forensicPendiente(row);
    }
  });

  const riesgoMunicipios =
    Object.values(municipioForenseMap)
      .sort(function (a, b) {
        return Number(b.foliosPendientes || 0) -
               Number(a.foliosPendientes || 0);
      });

  const topRiesgo =
    riesgoMunicipios.slice(0, 10);

  const clasificacionCount = {};

  expedientesForenses.forEach(function (row) {

    const key =
      forensicClasificacion(row);

    clasificacionCount[key] =
      Number(clasificacionCount[key] || 0) + 1;
  });

  const rankingClasificaciones =
    Object.keys(clasificacionCount)
      .map(function (key) {
        return {
          label: key,
          total: clasificacionCount[key]
        };
      })
      .sort(function (a, b) {
        return b.total - a.total;
      });


  const catalogoMunicipios = Array.from(
    new Set(
      municipios
        .map(function (row) { return row.municipio || row.oficina || ""; })
        .filter(Boolean)
    )
  ).sort();

  function matchFiltro(row) {
    const rowMunicipio = String(row.municipio || row.oficina || "");
    const rowUsuario = String(row.usuario || row.nombre || "");
    const allText = [
      row.municipio,
      row.oficina,
      row.usuario,
      row.nombre,
      row.causa,
      row.observaciones,
      row.descripcion,
      row.folios,
      row.message,
      row.type,
      row.severity,
      row.estatus
    ].join(" ").toLowerCase();

    if (municipio !== "TODOS" && rowMunicipio !== municipio) return false;
    if (usuario && !rowUsuario.toLowerCase().includes(usuario.toLowerCase())) return false;
    if (textoLibre && !allText.includes(textoLibre.toLowerCase())) return false;

    return true;
  }

  const municipiosFiltrados = municipios.filter(matchFiltro);
  const cancelacionesFiltradas = cancelaciones.filter(matchFiltro);
  const sinReporteFiltrado = sinReporte.filter(matchFiltro);
  const auditoriaFiltrada = auditoria.filter(matchFiltro);

  const casosCarryoverFiltrados =
    casosCarryoverV51.filter(function (row) {

      const rowMunicipio =
        String(row.municipio || "");

      const evidenceText =
        Array.isArray(row.evidencia)
          ? row.evidencia
              .map(function (item) {
                return [
                  item.usuario,
                  item.inicio,
                  item.fin,
                  item.rowActual,
                  item.rowSiguiente
                ].join(" ");
              })
              .join(" ")
          : "";

      const allText =
        [
          row.fecha,
          row.fechaSiguiente,
          row.municipio,
          evidenceText
        ]
          .join(" ")
          .toLowerCase();

      if (
        municipio !== "TODOS" &&
        rowMunicipio !== municipio
      ) {
        return false;
      }

      if (
        usuario &&
        !evidenceText
          .toLowerCase()
          .includes(usuario.toLowerCase())
      ) {
        return false;
      }

      if (
        textoLibre &&
        !allText.includes(textoLibre.toLowerCase())
      ) {
        return false;
      }

      return true;
    });

  const rankingEmitidas = municipiosFiltrados
    .slice()
    .sort(function (a, b) { return Number(b.emitidas || 0) - Number(a.emitidas || 0); });

  const rankingDiferencia = municipiosFiltrados
    .map(function (row) {
      return { ...row, diferenciaAbs: Math.abs(Number(row.diferencia || 0)) };
    })
    .sort(function (a, b) { return Number(b.diferenciaAbs || 0) - Number(a.diferenciaAbs || 0); });

  const rankingCancelaciones = municipiosFiltrados
    .slice()
    .sort(function (a, b) { return Number(b.canceladasDetalle || 0) - Number(a.canceladasDetalle || 0); });

  const municipioLider = rankingEmitidas[0] || {};
  const municipioMasDiferencia = rankingDiferencia[0] || {};
  const municipioMasCancelaciones = rankingCancelaciones[0] || {};

  const totalEmitidas = Number(summary.totalEmitidas || 0);
  const totalBitacora = Number(summary.totalFoliosConsumidos || 0);
  const diferencia = Number(summary.diferenciaEmisionBitacora || totalEmitidas - totalBitacora);
  const canceladasResumen = Number(summary.totalCanceladas || 0);
  const canceladasDetalle = Number(summary.totalCanceladasDetalle || cancelaciones.length || 0);
  const netasValidas = Number(summary.totalNetasValidas || totalEmitidas - canceladasResumen);
  const score = Number(summary.scoreOperativo || 0);

  const dictamen = buildFolioDictamen(folioMatches);

  if (loading) {
    return <LoaderPremiumLogo />;
}

  if (error) {
    return (
      <div className="folios-shell">
        <div className="state-box error">
          <strong>No se pudo cargar el módulo</strong>
          <p>{error}</p>
          <button type="button" onClick={function () { cargar(true); }}>Reintentar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="folios-shell">
      <header className="page-header">
        <div>
          <p>Inventario / Auditoría operativa</p>
          <h1>Control de Folios</h1>
          <span>
            Centro de control para emisión, bitácora, cancelaciones, búsqueda de folios y alertas ejecutivas.
          </span>
        </div>

        <div className="period-card">
          <span>Periodo fuente</span>
          <strong>{text(summary.fechaInicio)} - {text(summary.fechaFin)}</strong>
          <button type="button" onClick={function () { cargar(true); }}>Actualizar</button>
        </div>
      </header>

      <nav className="mode-tabs">
        <button
          type="button"
          className={activeTab === "executive" ? "active" : ""}
          onClick={function () { setActiveTab("executive"); }}
        >
          Resumen Ejecutivo
        </button>

        <button
          type="button"
          className={activeTab === "folio" ? "active" : ""}
          onClick={function () { setActiveTab("folio"); }}
        >
          Búsqueda Inteligente
        </button>

        <button
          type="button"
          className={activeTab === "audit" ? "active" : ""}
          onClick={function () { setActiveTab("audit"); }}
        >
          Auditoría Operativa
        </button>

        <button
          type="button"
          className={activeTab === "forensic" ? "active" : ""}
          onClick={function () {
            setActiveTab("forensic");
            setSelectedForensicCase(null);
          }}
        >
          Forense V5.1.2
        </button>
      </nav>

      {activeTab === "executive" ? (
        <section className="tab-layout executive-tab-layout">
          <FiltersPanel
            title="Periodo y filtros"
            fechaInicio={fechaInicio}
            fechaFin={fechaFin}
            setFechaInicio={setFechaInicio}
            setFechaFin={setFechaFin}
            municipio={municipio}
            setMunicipio={setMunicipio}
            usuario={usuario}
            setUsuario={setUsuario}
            textoLibre={textoLibre}
            setTextoLibre={setTextoLibre}
            municipios={catalogoMunicipios}
            onClear={limpiarFiltros}
            onApply={function () { cargar(true); }}
          />

          <main className="tab-main">
            <section className="kpi-grid">
              <Kpi label="Emitidas" value={fmt(totalEmitidas)} hint="Emisión diaria" />
              <Kpi label="Consumidas bitácora" value={fmt(totalBitacora)} hint="Entregadas - sobrantes" />
              <Kpi label="Diferencia" value={signed(diferencia)} tone={diferencia ? "danger" : "ok"} />
              <Kpi
                label="Canceladas"
                value={fmt(canceladasResumen) + " / " + fmt(canceladasDetalle)}
                hint="Resumen / detalle"
                tone="warn"
              />
              <Kpi label="Sin reporte" value={fmt(summary.municipiosSinReporte || sinReporte.length)} tone="warn" />
              <Kpi
                label="Anomalias bitacora"
                value={fmt(summary.totalAnomaliasBitacora || 0)}
                hint="Control documental"
                tone={Number(summary.totalAnomaliasBitacora || 0) > 0 ? "warn" : "ok"}
              />
              <Kpi label="Score" value={fmt(score) + "/100"} tone={score >= 80 ? "ok" : "warn"} />
            </section>

            <section className="executive-panel">
              <div className="panel-head">
                <div>
                  <p>Vista directiva</p>
                  <h2>Producción por municipio</h2>
                  <span>Resumen visual para Dirección General. Usa el rango de fechas para analizar días específicos.</span>
                </div>

                <div className="period-chip">
                  <span>Periodo aplicado</span>
                  <strong>{fechaInicio || "Inicio"} - {fechaFin || "Fin"}</strong>
                </div>
              </div>

              <div className="insight-grid">
                <div>
                  <span>Municipio con más emisión</span>
                  <strong>{text(municipioLider.municipio || municipioLider.oficina)}</strong>
                  <small>{fmt(municipioLider.emitidas)} emitidas</small>
                </div>

                <div>
                  <span>Mayor diferencia</span>
                  <strong>{text(municipioMasDiferencia.municipio || municipioMasDiferencia.oficina)}</strong>
                  <small>{signed(municipioMasDiferencia.diferencia)}</small>
                </div>

                <div>
                  <span>Más cancelaciones</span>
                  <strong>{text(municipioMasCancelaciones.municipio || municipioMasCancelaciones.oficina)}</strong>
                  <small>{fmt(municipioMasCancelaciones.canceladasDetalle)} cancelaciones</small>
                </div>
              </div>

              <div className="chart-grid">
                <BarChart
                  eyebrow="Producción"
                  title="Top municipios por licencias emitidas"
                  rows={rankingEmitidas}
                  valueKey="emitidas"
                />

                <BarChart
                  eyebrow="Riesgo operativo"
                  title="Municipios con mayor diferencia"
                  rows={rankingDiferencia}
                  valueKey="diferenciaAbs"
                />

                <BarChart
                  eyebrow="Cancelaciones"
                  title="Top municipios por cancelaciones"
                  rows={rankingCancelaciones}
                  valueKey="canceladasDetalle"
                />
              </div>
            </section>

            <section className="executive-panel compact-panel">
              <div className="panel-head small">
                <div>
                  <p>Alertas rápidas</p>
                  <h2>Lo que debe revisar Dirección</h2>
                </div>
              </div>

              <div className="alert-grid">
                <div>
                  <span>Oficinas sin reporte</span>
                  <strong>{fmt(sinReporteFiltrado.length)}</strong>
                  <small>{sinReporteFiltrado.slice(0, 3).map(function (row) { return row.municipio || row.oficina; }).join(" · ") || "Sin pendientes"}</small>
                </div>

                <div>
                  <span>Causa principal</span>
                  <strong>{text(causas[0] && (causas[0].label || causas[0].causa))}</strong>
                  <small>{fmt(causas[0] && causas[0].total)} cancelaciones</small>
                </div>

                <div>
                  <span>Usuario con más cancelaciones</span>
                  <strong>{text(usuariosCancelacion[0] && (usuariosCancelacion[0].label || usuariosCancelacion[0].usuario))}</strong>
                  <small>{fmt(usuariosCancelacion[0] && usuariosCancelacion[0].total)} registros</small>
                </div>
              </div>
            </section>
          </main>
        </section>
      ) : null}

      {activeTab === "folio" ? (
        <section className="folio-tab">
          <section className="folio-search-panel">
            <div>
              <p>Motor universal de búsqueda</p>
              <h2>Rastrear cualquier cosa</h2>
              <span>Busca folio, usuario, municipio, causa, estatus, rango o texto libre.</span>
            </div>

            <div className="folio-search-actions">
              <input
                value={folio}
                onChange={function (event) { setFolio(event.target.value); }}
                onKeyDown={function (event) {
                  if (event.key === "Enter") buscarFolio();
                }}
                placeholder="Ej. 404717, DIDIER, OTHON, FALLA IMPRESORA, 607451-607500"
              />

              <button type="button" onClick={buscarFolio} disabled={folioLoading || !folio}>
                {folioLoading ? "Buscando..." : "Buscar"}
              </button>
            </div>
          </section>

          <section className="folio-answer-panel">
            <div className="folio-answer-head">
              <div>
                <p>Resultado principal</p>
                <h2>{folioSearched ? folio : "Sin folio consultado"}</h2>
              </div>

              <div>
                <span>Coincidencias</span>
                <strong>{folioLoading ? "..." : folioSearched ? folioMatches.length : 0}</strong>
              </div>
            </div>

            {folioLoading ? (
              <div className="empty-box spacious">
                <strong>Consultando motor universal</strong>
                <p>Buscando coincidencias en folios, rangos, cancelaciones, municipios y auditoría.</p>
              </div>
            ) : null}

            {folioError ? (
              <div className="empty-box spacious">
                <strong>Error en búsqueda</strong>
                <p>{folioError}</p>
              </div>
            ) : null}

            {folioSearched && !folioLoading && !folioError && folioMatches.length === 0 ? (
              <div className="empty-box spacious">
                <strong>No se encontraron coincidencias</strong>
                <p>No aparece en las fuentes consultadas. Prueba con folio, municipio, usuario, causa o rango.</p>
              </div>
            ) : null}

            {folioSearched && !folioLoading && !folioError && folioMatches.length > 0 ? (
              <>
                <div className="folio-dictamen-grid">
                  <div className="dictamen-card primary">
                    <span>Responsable / coincidencia principal</span>
                    <strong>{text(dictamen.quienUso, "Sin responsable en bitácora")}</strong>
                    <small>
                      {text(dictamen.fuenteUso, "Fuente no disponible")}
                      {dictamen.rangoUso ? " / " + dictamen.rangoUso : ""}
                    </small>
                  </div>

                  <div className="dictamen-card">
                    <span>Estatus operativo</span>
                    <strong>{text(dictamen.estado)}</strong>
                    <small>{text(dictamen.causa)}</small>
                  </div>

                  <div className="dictamen-card">
                    <span>Municipio / oficina</span>
                    <strong>{text(dictamen.municipio)}</strong>
                    <small>{text(dictamen.fecha)}</small>
                  </div>

                  <div className="dictamen-card risk">
                    <span>Riesgo</span>
                    <strong>{text(dictamen.riesgo)}</strong>
                    <small>{text(dictamen.mensaje)}</small>
                  </div>
                </div>

                <div className="trace-title">
                  <p>Trazabilidad de la búsqueda</p>
                  <span>Se muestran todas las fuentes donde aparece la coincidencia.</span>
                </div>

                <div className="table-wrap">
                  <table className="folio-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Municipio / oficina</th>
                        <th>Usuario / responsable</th>
                        <th>Fuente</th>
                        <th>Causa / rango</th>
                        <th>Estatus</th>
                      </tr>
                    </thead>

                    <tbody>
                      {folioMatches.map(function (item, index) {
                        return (
                          <tr key={String(index)}>
                            <td>{text(item.fecha)}</td>
                            <td><strong>{text(item.municipio || item.oficina)}</strong></td>
                            <td>{text(item.usuario)}</td>
                            <td>{text(item.fuente)}</td>
                            <td>{text(item.causa || item.rango || item.folios)}</td>
                            <td>
                              <Pill tone={isCancelacion(item) ? "danger" : ""}>
                                {text(item.estatus, "ENCONTRADO")}
                              </Pill>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </section>
        </section>
      ) : null}

      {activeTab === "audit" ? (
        <section className="tab-layout">
          <FiltersPanel
            title="Filtros de auditoría"
            fechaInicio={fechaInicio}
            fechaFin={fechaFin}
            setFechaInicio={setFechaInicio}
            setFechaFin={setFechaFin}
            municipio={municipio}
            setMunicipio={setMunicipio}
            usuario={usuario}
            setUsuario={setUsuario}
            textoLibre={textoLibre}
            setTextoLibre={setTextoLibre}
            municipios={catalogoMunicipios}
            onClear={limpiarFiltros}
            onApply={function () { cargar(true); }}
          />

          <main className="tab-main">
            <section className="executive-panel">
              <button
                className="section-toggle"
                type="button"
                onClick={function () { setShowRanking(!showRanking); }}
              >
                <span>Ranking de conciliación</span>
                <strong>{showRanking ? "Ocultar" : "Ver"} {fmt(municipiosFiltrados.length)}</strong>
              </button>

              {showRanking ? (
                <div className="table-wrap">
                  <table className="folio-table">
                    <thead>
                      <tr>
                        <th>Municipio / oficina</th>
                        <th>Riesgo</th>
                        <th>Emitidas</th>
                        <th>Bitácora</th>
                        <th>Diferencia</th>
                        <th>Canceladas</th>
                        <th>Sin reporte</th>
                        <th>Anomalias</th>
                      </tr>
                    </thead>

                    <tbody>
                      {municipiosFiltrados.slice(0, 60).map(function (row, index) {
                        return (
                          <tr key={String(index)}>
                            <td><strong>{text(row.municipio || row.oficina)}</strong></td>
                            <td>{text(row.riesgo)}</td>
                            <td>{fmt(row.emitidas)}</td>
                            <td>{fmt(row.bitacora)}</td>
                            <td className={Number(row.diferencia || 0) !== 0 ? "danger-text" : ""}>
                              {signed(row.diferencia)}
                            </td>
                            <td>{fmt(row.canceladasResumen)} / {fmt(row.canceladasDetalle)}</td>
                            <td>{fmt(row.sinReporte)}</td>
                            <td className={Number(row.anomaliasBitacora || 0) > 0 ? "danger-text" : ""}>
                              {fmt(row.anomaliasBitacora || 0)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </section>

            <section className="executive-panel">
              <button
                className="section-toggle"
                type="button"
                onClick={function () { setShowCancelaciones(!showCancelaciones); }}
              >
                <span>Cancelaciones</span>
                <strong>{showCancelaciones ? "Ocultar" : "Ver"} {fmt(cancelacionesFiltradas.length)}</strong>
              </button>

              {showCancelaciones ? (
                <div className="table-wrap">
                  <table className="folio-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Municipio</th>
                        <th>Usuario</th>
                        <th>Causa</th>
                        <th>Folios</th>
                        <th>Total</th>
                      </tr>
                    </thead>

                    <tbody>
                      {cancelacionesFiltradas.slice(0, 100).map(function (row, index) {
                        return (
                          <tr key={String(index)}>
                            <td>{text(row.fecha)}</td>
                            <td>{text(row.municipio || row.oficina)}</td>
                            <td>{text(row.usuario || row.nombre)}</td>
                            <td>{text(row.causa || row.observaciones)}</td>
                            <td>{text(row.folios)}</td>
                            <td>{fmt(row.total)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </section>

            <section className="executive-panel">
              <button
                className="section-toggle"
                type="button"
                onClick={function () { setShowAuditoria(!showAuditoria); }}
              >
                <span>Auditoría y sin reporte</span>
                <strong>{showAuditoria ? "Ocultar" : "Ver"} {fmt(auditoriaFiltrada.length + sinReporteFiltrado.length)}</strong>
              </button>

              {showAuditoria ? (
                <div className="audit-list">
                  {sinReporteFiltrado.map(function (row, index) {
                    return (
                      <div className="audit-card" key={"sr-" + index}>
                        <span>SIN REPORTE</span>
                        <strong>{text(row.municipio || row.oficina)}</strong>
                        <p>{text(row.estatus)}</p>
                        <small>{text(row.fecha)}</small>
                      </div>
                    );
                  })}

                  {auditoriaFiltrada.slice(0, 100).map(function (item, index) {
                    return (
                      <div className="audit-card" key={"au-" + index}>
                        <span>{text(item.severity)} / {text(item.type)}</span>
                        <strong>{text(item.municipio)}</strong>
                        <p>{text(item.message)}</p>
                        <small>{text(item.fecha)}</small>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </section>
          </main>
        </section>
      ) : null}

      {activeTab === "forensic" ? (
        <section className="forensic-console">

          <section className="forensic-hero">
            <div>
              <p>Auditoría integral de folios</p>
              <h2>Consola Forense</h2>
              <span>
                Expedientes, evidencia, clasificaciones, riesgos,
                conciliaciones y trazabilidad del periodo seleccionado.
              </span>
            </div>

            <div className="forensic-version">
              <span>Periodo analizado</span>
              <strong>{text(summary.fechaInicio)} → {text(summary.fechaFin)}</strong>
              <Pill tone={integridadV51 === "OK" ? "" : "danger"}>
                {integridadV51 === "OK" ? "ANÁLISIS ÍNTEGRO" : "REVISAR"}
              </Pill>
            </div>
          </section>


          <section className="forensic-kpis">

            <Kpi
              label="Pendientes iniciales"
              value={fmt(pendienteV49)}
              hint="Antes de conciliaciones finales"
              tone="warn"
            />

            <Kpi
              label="Folios aclarados"
              value={fmt(reduccionV51)}
              hint="Explicados con evidencia"
              tone="ok"
            />

            <Kpi
              label="Pendientes reales"
              value={fmt(pendienteV51)}
              hint="Aún requieren revisión"
              tone="warn"
            />

            <Kpi
              label="Expedientes abiertos"
              value={fmt(expedientesPendientes.length)}
              hint="Casos aún no conciliados"
              tone={expedientesPendientes.length > 0 ? "warn" : "ok"}
            />

            <Kpi
              label="Expedientes conciliados"
              value={fmt(expedientesConciliados.length)}
              hint="Casos cerrados con evidencia"
              tone="ok"
            />

            <Kpi
              label="Integridad"
              value={integridadV51 === "OK" ? "CORRECTO" : "REVISAR"}
              hint="Control matemático"
              tone={integridadV51 === "OK" ? "ok" : "danger"}
            />

          </section>


          <section className="forensic-dashboard">

            <div className="forensic-card">

              <div className="panel-head small">
                <div>
                  <p>Distribución de riesgo</p>
                  <h2>Municipios con más pendientes</h2>
                </div>
              </div>

              <div className="forensic-ranking-list">

                {topRiesgo.length === 0 ? (
                  <div className="empty-box">
                    Sin expedientes para este periodo.
                  </div>
                ) : null}

                {topRiesgo.map(function (row, index) {

                  const max =
                    Number(topRiesgo[0] && topRiesgo[0].foliosPendientes || 0);

                  const width =
                    max > 0
                      ? Math.max(
                          3,
                          Math.round(
                            Number(row.foliosPendientes || 0) /
                            max *
                            100
                          )
                        )
                      : 0;

                  return (
                    <div
                      className="forensic-ranking-row"
                      key={row.municipio + "-" + index}
                    >

                      <div className="forensic-ranking-head">
                        <strong>{row.municipio}</strong>
                        <span>{fmt(row.foliosPendientes)} folios</span>
                      </div>

                      <div className="exec-track">
                        <i style={{ width: width + "%" }} />
                      </div>

                      <small>
                        {fmt(row.pendientes)} expedientes abiertos ·
                        {" "}
                        {fmt(row.conciliados)} conciliados
                      </small>

                    </div>
                  );
                })}

              </div>

            </div>


            <div className="forensic-card">

              <div className="panel-head small">
                <div>
                  <p>Patrones detectados</p>
                  <h2>Clasificaciones forenses</h2>
                </div>
              </div>

              <div className="forensic-classification-list">

                {rankingClasificaciones
                  .slice(0, 12)
                  .map(function (row, index) {

                    return (
                      <div
                        className="forensic-classification-row"
                        key={row.label + "-" + index}
                      >
                        <strong>{row.label}</strong>
                        <span>{fmt(row.total)}</span>
                      </div>
                    );
                  })}

              </div>

            </div>

          </section>


          <section className="forensic-investigation-panel">

            <div className="panel-head">

              <div>
                <p>Investigación forense</p>
                <h2>Expedientes</h2>
                <span>
                  Consulta expedientes pendientes y conciliados,
                  clasificación, nivel de evidencia y detalle documental.
                </span>
              </div>

              <div className="forensic-mode-tabs">

                <button
                  type="button"
                  className={forensicMode === "pendientes" ? "active" : ""}
                  onClick={function () {
                    setForensicMode("pendientes");
                    setSelectedForensicCase(null);
                  }}
                >
                  Pendientes
                  {" "}
                  ({fmt(expedientesPendientesFiltrados.length)})
                </button>

                <button
                  type="button"
                  className={forensicMode === "conciliados" ? "active" : ""}
                  onClick={function () {
                    setForensicMode("conciliados");
                    setSelectedForensicCase(null);
                  }}
                >
                  Conciliados
                  {" "}
                  ({fmt(expedientesConciliadosFiltrados.length)})
                </button>

              </div>

            </div>


            <div className="forensic-filter-row forensic-filter-row-wide">

              <label className="field">
                <span>Municipio</span>

                <select
                  value={municipio}
                  onChange={function (event) {
                    setMunicipio(event.target.value);
                    setSelectedForensicCase(null);
                  }}
                >
                  <option value="TODOS">Todos</option>

                  {catalogoMunicipios.map(function (item) {
                    return (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    );
                  })}

                </select>

              </label>


              <label className="field">
                <span>Clasificación</span>

                <select
                  value={forensicClassification}
                  onChange={function (event) {
                    setForensicClassification(event.target.value);
                    setSelectedForensicCase(null);
                  }}
                >
                  <option value="TODAS">
                    Todas
                  </option>

                  {forensicClassifications.map(function (item) {
                    return (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    );
                  })}

                </select>

              </label>


              <label className="field">
                <span>Buscar expediente</span>

                <input
                  value={textoLibre}
                  onChange={function (event) {
                    setTextoLibre(event.target.value);
                    setSelectedForensicCase(null);
                  }}
                  placeholder="Municipio, clasificación, expediente..."
                />

              </label>

            </div>


            <div className="table-wrap">

              <table className="folio-table forensic-master-table">

                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Municipio</th>
                    <th>Emitidas</th>
                    <th>Bitácora</th>
                    <th>Diferencia</th>
                    <th>Pendiente</th>
                    <th>Clasificación</th>
                    <th>Evidencia</th>
                    <th>Estado</th>
                    <th>Acción</th>
                  </tr>
                </thead>


                <tbody>

                  {(
                    forensicMode === "pendientes"
                      ? expedientesPendientesFiltrados
                      : expedientesConciliadosFiltrados
                  )
                    .slice(0, 250)
                    .map(function (row, index) {

                      const estado =
                        forensicEstado(row);

                      const conciliado =
                        estado.includes("CONCILIADO");

                      const diferencia =
                        Number(
                          row.diferenciaOriginalRaw ||
                          row.diferenciaProbable ||
                          row.diferenciaDocumental ||
                          0
                        );

                      return (
                        <tr
                          key={
                            row.expedienteId ||
                            String(index)
                          }
                        >

                          <td>{text(row.fecha)}</td>

                          <td>
                            <strong>{text(row.municipio)}</strong>
                          </td>

                          <td>
                            {fmt(
                              row.emisionRaw ||
                              row.emisionProbable ||
                              row.emisionProbableDocumental
                            )}
                          </td>

                          <td>
                            {fmt(row.bitacora)}
                          </td>

                          <td className={diferencia !== 0 ? "danger-text" : ""}>
                            {signed(diferencia)}
                          </td>

                          <td>
                            {fmt(forensicPendiente(row))}
                          </td>

                          <td>
                            <strong>
                              {forensicClasificacion(row)}
                            </strong>
                          </td>

                          <td>
                            {forensicNivel(row)}
                          </td>

                          <td>
                            <Pill tone={conciliado ? "" : "danger"}>
                              {estado}
                            </Pill>
                          </td>

                          <td>
                            <button
                              className="forensic-detail-btn"
                              type="button"
                              onClick={function () {
                                setSelectedForensicCase(row);
                              }}
                            >
                              Investigar
                            </button>
                          </td>

                        </tr>
                      );
                    })}

                </tbody>

              </table>

            </div>

          </section>


          {selectedForensicCase ? (

            <section className="forensic-case-file">

              <div className="panel-head">

                <div>
                  <p>Expediente forense</p>
                  <h2>{text(selectedForensicCase.municipio)}</h2>
                  <span>
                    {text(selectedForensicCase.expedienteId)}
                  </span>
                </div>

                <button
                  className="forensic-close-btn"
                  type="button"
                  onClick={function () {
                    setSelectedForensicCase(null);
                  }}
                >
                  Cerrar expediente
                </button>

              </div>


              <div className="forensic-case-summary">

                <div className="dictamen-card primary">
                  <span>Fecha</span>
                  <strong>{text(selectedForensicCase.fecha)}</strong>
                </div>

                <div className="dictamen-card">
                  <span>Emisión oficial</span>
                  <strong>
                    {fmt(
                      selectedForensicCase.emisionRaw ||
                      selectedForensicCase.emisionProbable ||
                      selectedForensicCase.emisionProbableDocumental
                    )}
                  </strong>
                </div>

                <div className="dictamen-card">
                  <span>Bitácora</span>
                  <strong>{fmt(selectedForensicCase.bitacora)}</strong>
                </div>

                <div className="dictamen-card risk">
                  <span>Pendiente</span>
                  <strong>{fmt(forensicPendiente(selectedForensicCase))}</strong>
                </div>

                <div className="dictamen-card">
                  <span>Clasificación</span>
                  <strong>{forensicClasificacion(selectedForensicCase)}</strong>
                </div>

                <div className="dictamen-card">
                  <span>Nivel de evidencia</span>
                  <strong>{forensicNivel(selectedForensicCase)}</strong>
                </div>

              </div>


              <div className="forensic-case-grid">

                <div className="forensic-evidence-box">

                  <h3>Diagnóstico</h3>

                  <div className="forensic-info-row">
                    <span>Estado</span>
                    <strong>
                      {forensicEstado(selectedForensicCase)}
                    </strong>
                  </div>

                  <div className="forensic-info-row">
                    <span>Causa probable</span>
                    <strong>
                      {text(
                        selectedForensicCase.causaProbableOriginal,
                        "Sin causa documentada"
                      )}
                    </strong>
                  </div>

                  <div className="forensic-info-row">
                    <span>Acción recomendada</span>
                    <strong>
                      {text(
                        selectedForensicCase.accionForense ||
                        selectedForensicCase.accionOriginal,
                        "Sin acción definida"
                      )}
                    </strong>
                  </div>

                </div>


                <div className="forensic-evidence-box">

                  <h3>Rango documental</h3>

                  <div className="forensic-info-row">
                    <span>Inicio</span>
                    <strong>
                      {fmt(
                        selectedForensicCase.rangoActualV46 &&
                        selectedForensicCase.rangoActualV46.inicio
                      )}
                    </strong>
                  </div>

                  <div className="forensic-info-row">
                    <span>Fin</span>
                    <strong>
                      {fmt(
                        selectedForensicCase.rangoActualV46 &&
                        selectedForensicCase.rangoActualV46.fin
                      )}
                    </strong>
                  </div>

                  <div className="forensic-info-row">
                    <span>Segmentos</span>
                    <strong>
                      {fmt(
                        selectedForensicCase.rangoActualV46 &&
                        selectedForensicCase.rangoActualV46.segmentosCount
                      )}
                    </strong>
                  </div>

                  <div className="forensic-info-row">
                    <span>Huecos internos</span>
                    <strong>
                      {
                        selectedForensicCase.rangoActualV46 &&
                        selectedForensicCase.rangoActualV46.tieneHuecosInternos
                          ? "SÍ"
                          : "NO"
                      }
                    </strong>
                  </div>

                </div>


                <div className="forensic-evidence-box">

                  <h3>Continuidad</h3>

                  <div className="forensic-info-row">
                    <span>Rango anterior</span>
                    <strong>
                      {selectedForensicCase.continuidadAnteriorV46 ? "SÍ" : "NO"}
                    </strong>
                  </div>

                  <div className="forensic-info-row">
                    <span>Rango siguiente</span>
                    <strong>
                      {selectedForensicCase.continuidadSiguienteV46 ? "SÍ" : "NO"}
                    </strong>
                  </div>

                  <div className="forensic-info-row">
                    <span>Cancelaciones</span>
                    <strong>
                      {fmt(selectedForensicCase.canceladasV47)}
                    </strong>
                  </div>

                  <div className="forensic-info-row">
                    <span>Causa de cierre</span>
                    <strong>
                      {text(
                        selectedForensicCase.causaCierreV48,
                        "Sin cierre"
                      )}
                    </strong>
                  </div>

                </div>

              </div>


              <div className="forensic-evidence-columns">

                <div className="forensic-evidence-box">

                  <h3>Evidencia causal</h3>

                  {(
                    Array.isArray(selectedForensicCase.evidenciaCausal)
                      ? selectedForensicCase.evidenciaCausal
                      : []
                  ).map(function (item, index) {
                    return (
                      <div
                        className="forensic-evidence-line"
                        key={"causal-" + index}
                      >
                        {item}
                      </div>
                    );
                  })}

                </div>


                <div className="forensic-evidence-box">

                  <h3>Evidencia de clasificación</h3>

                  {(
                    Array.isArray(selectedForensicCase.evidenciaV47)
                      ? selectedForensicCase.evidenciaV47
                      : Array.isArray(selectedForensicCase.evidenciaV46)
                        ? selectedForensicCase.evidenciaV46
                        : []
                  ).map(function (item, index) {
                    return (
                      <div
                        className="forensic-evidence-line"
                        key={"clas-" + index}
                      >
                        {item}
                      </div>
                    );
                  })}

                </div>

              </div>


              {selectedForensicCase.rangoActualV46 &&
              Array.isArray(selectedForensicCase.rangoActualV46.huecosInternos) &&
              selectedForensicCase.rangoActualV46.huecosInternos.length > 0 ? (

                <div className="forensic-evidence-box forensic-gaps">

                  <h3>Huecos internos del rango</h3>

                  <div className="table-wrap">

                    <table className="folio-table">

                      <thead>
                        <tr>
                          <th>Desde</th>
                          <th>Hasta</th>
                          <th>Total</th>
                        </tr>
                      </thead>

                      <tbody>

                        {selectedForensicCase.rangoActualV46.huecosInternos
                          .map(function (gap, index) {

                            return (
                              <tr key={"gap-" + index}>
                                <td>{fmt(gap.desde)}</td>
                                <td>{fmt(gap.hasta)}</td>
                                <td>{fmt(gap.total)}</td>
                              </tr>
                            );
                          })}

                      </tbody>

                    </table>

                  </div>

                </div>

              ) : null}


              <div className="forensic-history">

                <h3>Historial de conciliación</h3>

                <div className="forensic-history-grid">

                  <div>
                    <span>V4.4</span>
                    <strong>
                      {fmt(selectedForensicCase.pendienteForenseV44)}
                    </strong>
                  </div>

                  <div>
                    <span>V4.7</span>
                    <strong>
                      {fmt(selectedForensicCase.pendienteForenseV47)}
                    </strong>
                  </div>

                  <div>
                    <span>V4.8</span>
                    <strong>
                      {fmt(selectedForensicCase.pendienteForenseV48)}
                    </strong>
                  </div>

                  <div>
                    <span>Estado final</span>
                    <strong>
                      {forensicEstado(selectedForensicCase)}
                    </strong>
                  </div>

                </div>

              </div>

            </section>

          ) : null}


          <section className="forensic-carryover-section">

            <div className="panel-head">
              <div>
                <p>Continuidad entre jornadas</p>
                <h2>Casos aclarados por reutilización documentada</h2>
                <span>
                  Folios localizados como continuidad exacta en la siguiente
                  jornada operativa.
                </span>
              </div>

              <div className="period-chip">
                <span>Casos</span>
                <strong>{fmt(casosCarryoverFiltrados.length)}</strong>
              </div>
            </div>


            <div className="table-wrap">

              <table className="folio-table">

                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Siguiente</th>
                    <th>Municipio</th>
                    <th>Diferencia</th>
                    <th>Aclarados</th>
                    <th>Pendiente final</th>
                    <th>Estado</th>
                    <th>Detalle</th>
                  </tr>
                </thead>

                <tbody>

                  {casosCarryoverFiltrados.map(function (row, index) {

                    return (
                      <tr key={"co-" + index}>

                        <td>{text(row.fecha)}</td>
                        <td>{text(row.fechaSiguiente)}</td>

                        <td>
                          <strong>{text(row.municipio)}</strong>
                        </td>

                        <td>{fmt(row.pendienteAntes)}</td>

                        <td className="forensic-reduction">
                          {fmt(row.reduccion)}
                        </td>

                        <td>{fmt(row.pendienteDespues)}</td>

                        <td>
                          <Pill>
                            {row.conciliado ? "CONCILIADO" : "PENDIENTE"}
                          </Pill>
                        </td>

                        <td>
                          <button
                            className="forensic-detail-btn"
                            type="button"
                            onClick={function () {
                              setSelectedForensicCase(row);
                            }}
                          >
                            Evidencia
                          </button>
                        </td>

                      </tr>
                    );
                  })}

                </tbody>

              </table>

            </div>

          </section>

        </section>
      ) : null}
    </div>
  );
}
